import React, { Component } from 'react';
import { PHONE_CAPTION_LABELS, isPhoneCaptionId, phoneCaptionSpeaker } from '../../interpreter-phone/phoneCaptions';
import { calledPartyName } from '../../vrs-layout/callClock';
import { readStoredActiveCall } from '../../vrs-layout/storedCall';
import { getCaptionPreferences } from '../captionPreferences';
/**
 * Human captioners publish a conversational track; only the non-default ones
 * are worth surfacing as a line label.
 */
const CAPTION_TRACK_LABELS = {
    deaf: 'Deaf',
    hearing: 'Hearing',
    interpreter: 'Interpreter'
};
/**
 * Abstract React {@code Component} which can display speech-to-text results
 * from Jigasi as subtitles.
 */
export class AbstractCaptions extends Component {
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { _displaySubtitles, _requestingSubtitles, _transcripts } = this.props;
        if (!_requestingSubtitles || !_displaySubtitles || !_transcripts || !_transcripts.size) {
            return null;
        }
        const paragraphs = [];
        // @ts-ignore
        for (const [id, transcript] of _transcripts ?? []) {
            paragraphs.push(this._renderParagraph(id, transcript));
        }
        // @ts-ignore
        return this._renderSubtitlesContainer(paragraphs);
    }
    /**
     * Renders the transcription text.
     *
     * @abstract
     * @param {string} _id - The ID of the transcript message from which the
     * {@code text} has been created.
     * @param {Object} _transcript - Subtitles text and optional tone metadata.
     * @protected
     * @returns {ReactElement} - The React element which displays the text.
     */
    _renderParagraph(_id, _transcript) {
        return React.createElement(React.Fragment, null);
    }
    /**
     * Renders the subtitles container.
     *
     * @abstract
     * @param {Array<ReactElement>} _el - An array of elements created
     * for each subtitle using the {@link _renderParagraph} method.
     * @protected
     * @returns {ReactElement} - The subtitles container.
     */
    _renderSubtitlesContainer(_el) {
        return React.createElement(React.Fragment, null);
    }
}
/**
 * Formats the transcript messages into text by prefixing participant's name to
 * avoid duplicating the effort on platform specific component.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Map<string, ICaptionTranscript>} - Formatted transcript subtitles
 * mapped by transcript message IDs.
 */
function _constructTranscripts(state, t) {
    const { _transcriptMessages } = state['features/subtitles'];
    const captionPreferences = getCaptionPreferences();
    const transcripts = new Map();
    const phoneLabels = t ? {
        caller: t('vrs.phoneCaptions.caller'),
        youSpoken: t('vrs.phoneCaptions.youSpoken'),
        youVoiced: t('vrs.phoneCaptions.youVoiced')
    } : PHONE_CAPTION_LABELS;
    // The hearing party by the contact name the client called, when the
    // stored call for this room has one (vrs-layout/callClock.ts).
    let contactName;
    for (const [id, transcriptMessage] of _transcriptMessages) {
        if (transcriptMessage && isPhoneCaptionId(id)) {
            // A phone caption: the speaker comes from its track ("Caller",
            // "You · voiced"), never "Phone (Hearing)". The hearing party is
            // the contact the client called, else "Caller" (a typed number).
            if (contactName === undefined) {
                contactName = calledPartyName({
                    roomName: state['features/base/conference']?.room,
                    storedCall: readStoredActiveCall()
                });
            }
            const body = transcriptMessage.final
                || `${transcriptMessage.stable || ''}${transcriptMessage.unstable || ''}`;
            const speaker = phoneCaptionSpeaker(transcriptMessage.track, phoneLabels, contactName);
            transcripts.set(id, {
                body,
                interim: !transcriptMessage.final,
                phone: true,
                speaker,
                text: captionPreferences.showSpeakerNames ? `${speaker}: ${body}` : body,
                tone: transcriptMessage.captionTone
            });
        }
        else if (transcriptMessage) {
            let text = '';
            if (captionPreferences.showSpeakerNames) {
                const { name } = transcriptMessage.participant;
                const track = String(transcriptMessage.track);
                const trackLabel = ['deaf', 'hearing', 'interpreter'].includes(track)
                    ? t ? t(`captionTranscript.tracks.${track}`) : CAPTION_TRACK_LABELS[track]
                    : '';
                const speaker = typeof name === 'string' && name.trim()
                    ? name : t ? t('captionTranscript.unknownSpeaker') : 'Unknown speaker';
                text = `${speaker}${trackLabel ? ` (${trackLabel})` : ''}: `;
            }
            if (transcriptMessage.final) {
                text += transcriptMessage.final;
            }
            else {
                const stable = transcriptMessage.stable || '';
                const unstable = transcriptMessage.unstable || '';
                text += stable + unstable;
            }
            transcripts.set(id, {
                text,
                tone: transcriptMessage.captionTone
            });
        }
    }
    return transcripts;
}
/**
 * Maps the transcriptionSubtitles in the redux state to the associated props of
 * {@code AbstractCaptions}.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {{
 *     _requestingSubtitles: boolean,
 *     _transcripts: Map<string, ICaptionTranscript>
 * }}
 */
export function _abstractMapStateToProps(state, ownProps) {
    const { _displaySubtitles, _requestingSubtitles } = state['features/subtitles'];
    const transcripts = _constructTranscripts(state, ownProps?.t);
    return {
        _captionPreferences: getCaptionPreferences(),
        _displaySubtitles,
        _requestingSubtitles,
        // avoid re-renders by setting to prop new empty Map instances.
        _transcripts: transcripts.size === 0 ? undefined : transcripts
    };
}
